import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Invite-user modal — wraps the POST /admin/users endpoint.
 *
 * Shadow-JIT contract (ADR-008): we insert a row with status='invited'
 * and entra_object_id=NULL. On first Microsoft sign-in, the backend
 * matches by email and flips it to active. The admin doesn't need to
 * know any of that — they enter the email + display name + role(s).
 *
 * Validation rules (mirroring the server):
 *   - user_type=internal → client_id MUST be empty
 *   - user_type=customer|vendor → client_id REQUIRED
 *   - email is lowercased server-side; we lowercase on submit for the
 *     409-friendly "already exists" check.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from '@/lib/i18n';
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { ApiError, api } from '@/lib/api';
import { EntraUserPicker } from './EntraUserPicker';
import { RolePicker } from './RolePicker';
export function InviteUserModal({ open, onClose, }) {
    const qc = useQueryClient();
    const toast = useToast();
    const [email, setEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [phone, setPhone] = useState('');
    const [userType, setUserType] = useState('internal');
    const [clientId, setClientId] = useState('');
    const [roleIds, setRoleIds] = useState([]);
    const clients = useQuery({
        queryKey: ['clients'],
        queryFn: () => api.get('/clients'),
        enabled: open,
    });
    const reset = () => {
        setEmail('');
        setDisplayName('');
        setPhone('');
        setUserType('internal');
        setClientId('');
        setRoleIds([]);
    };
    const invite = useMutation({
        mutationFn: (body) => api.post('/admin/users', body),
        onSuccess: (user) => {
            qc.invalidateQueries({ queryKey: ['admin-users'] });
            toast.success('Invitation created', `${user.email} will activate on their first Microsoft sign-in.`);
            reset();
            onClose();
        },
        onError: (err) => {
            toast.error(t('Could not invite user'), err.message);
        },
    });
    const needsClient = userType !== 'internal';
    const canSubmit = email.trim().length > 0 &&
        displayName.trim().length > 0 &&
        (!needsClient || clientId !== '');
    return (_jsx(Modal, { open: open, onClose: onClose, title: t('Invite user'), size: "lg", children: _jsxs("form", { onSubmit: (e) => {
                e.preventDefault();
                if (!canSubmit)
                    return;
                const body = {
                    email: email.trim().toLowerCase(),
                    display_name: displayName.trim(),
                    user_type: userType,
                    role_ids: roleIds,
                };
                if (phone.trim())
                    body.phone = phone.trim();
                if (needsClient && clientId)
                    body.client_id = clientId;
                invite.mutate(body);
            }, className: "space-y-5", children: [_jsxs("div", { className: "grid gap-4 sm:grid-cols-2", children: [_jsx(Field, { label: t('Email'), required: true, hint: userType === 'internal'
                                ? 'Start typing — the dropdown searches your Entra ID directory.'
                                : 'Must match the email this person uses to sign in with Microsoft.', children: userType === 'internal' ? (_jsx(EntraUserPicker, { email: email, onEmailChange: setEmail, onPick: (u) => {
                                    setEmail(u.email);
                                    // Always overwrite display_name with the picked user's
                                    // name. Earlier this only set displayName when it was
                                    // empty, which caused stale state: pick A → name=A,
                                    // pick B → name STAYED as A (because the field wasn't
                                    // empty). Admins can still edit display name after
                                    // picking — but the default reflects the latest pick.
                                    setDisplayName(u.display_name);
                                    // Phone is admin-entered (Entra picker doesn't return it).
                                    // Reset to '' so the previous pick's phone (if admin
                                    // typed one) doesn't leak across pick changes.
                                    setPhone('');
                                } })) : (_jsx(Input, { type: "email", value: email, onChange: (e) => setEmail(e.target.value), placeholder: t('person@company.com'), required: true, autoFocus: true })) }), _jsx(Field, { label: t('Display name'), required: true, children: _jsx(Input, { value: displayName, onChange: (e) => setDisplayName(e.target.value), placeholder: t('Alex Morgan'), required: true }) }), _jsx(Field, { label: t('Phone'), hint: t('E.164 format (e.g. +919876543210). Optional.'), error: 
                            // E.164 inline hint (Sprint 1.2.x). Same regex shape
                            // as the backend `_E164` constant; once the user
                            // types ANYthing it's validated, but blank stays OK.
                            phone.trim() && !/^\+[1-9]\d{7,14}$/.test(phone.trim())
                                ? 'Phone must be E.164 (e.g. +919876543210). No spaces or punctuation.'
                                : undefined, children: _jsx(Input, { value: phone, onChange: (e) => setPhone(e.target.value), placeholder: t('+44 ...'), "aria-invalid": !!phone.trim() && !/^\+[1-9]\d{7,14}$/.test(phone.trim()) }) }), _jsx(Field, { label: t('User type'), required: true, children: _jsxs(Select, { value: userType, onChange: (e) => {
                                    const next = e.target.value;
                                    setUserType(next);
                                    if (next === 'internal')
                                        setClientId('');
                                }, children: [_jsx("option", { value: "internal", children: t('Internal (Styxtech staff)') }), _jsx("option", { value: "customer", children: t('Customer User') }), _jsx("option", { value: "vendor", children: t('Vendor') })] }) })] }), needsClient && (_jsx(Field, { label: t('Client'), required: true, hint: t('Customer Users and Vendors are scoped to a single client organization.'), children: _jsxs(Select, { value: clientId, onChange: (e) => setClientId(e.target.value), disabled: clients.isLoading, required: true, children: [_jsx("option", { value: "", children: "\u2014 Select a client \u2014" }), (clients.data ?? []).map((c) => (_jsx("option", { value: c.id, children: c.name }, c.id)))] }) })), _jsxs("div", { children: [_jsx("div", { className: "mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300", children: t('Roles') }), _jsx("div", { className: "mb-2 text-xs text-slate-500", children: "Pick one or more. Roles determine which screens and actions the user can access." }), _jsx(RolePicker, { value: roleIds, onChange: setRoleIds })] }), _jsxs("div", { className: "flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800", children: [_jsx(Button, { type: "button", variant: "ghost", onClick: onClose, disabled: invite.isPending, children: t('Cancel') }), _jsx(Button, { type: "submit", disabled: !canSubmit || invite.isPending, children: invite.isPending ? 'Sending invite…' : 'Send invite' })] })] }) }));
}
